import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MONTHS } from '@ee/common/constants';
import { RENT_HISTORY_MAX_ROWS, rentHistoryRow } from './intake-form-controls';

/**
 * Rent owed month by month — v1's `ee-past-due-rent-history`, on the v2 form.
 *
 * One row per month: the month, the year and the amount, each required, no month twice, and at least
 * one row (the first is added on sight, as v1 did, so the question is never an empty list with an Add
 * button). The rows are the answer — `past_due_rent_history` on the case — and their sum is Rent Owed,
 * written at submit (`toSubmissionParts`) and counted by the section total.
 *
 * Standalone and bound to a `FormArray` the form built, rather than v1's version that reached into a
 * parent group by name, so the renderer can hand it a control like any other placement.
 */
/** The month a block added on sight, per array: a later block (the page revisited) can still take it back. */
const STARTING_ROW = new WeakMap<FormArray, FormGroup>();

@Component({
  selector: 'ee-rent-history-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule],
  template: `
    <fieldset class="rent-history" [class.compact]="compact">
      <legend class="rent-history__legend">{{ label }}{{ required ? ' *' : '' }}</legend>
      <p class="rent-history__lead">{{ hint || 'Enter every month for which rent is owed.' }}</p>
      @for (row of rows.controls; track row; let i = $index) {
        <div class="rent-history__row" [formGroup]="row">
          <mat-form-field appearance="outline" [class.compact]="compact">
            <mat-label>Month</mat-label>
            <mat-select formControlName="month" required>
              @for (month of months; track month; let m = $index) {
                <mat-option [value]="m">{{ month }}</mat-option>
              }
            </mat-select>
            @if (row.get('month')?.hasError('required')) {
              <mat-error>Please select a month.</mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" [class.compact]="compact">
            <mat-label>Year</mat-label>
            <mat-select formControlName="year" required>
              @for (year of years; track year) {
                <mat-option [value]="year">{{ year }}</mat-option>
              }
            </mat-select>
            @if (row.get('year')?.hasError('required')) {
              <mat-error>Please select a year.</mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" [class.compact]="compact">
            <mat-label>Amount</mat-label>
            <span matPrefix>$ &nbsp;</span>
            <input matInput type="number" formControlName="amount" required autocomplete="off" />
            @if (row.get('amount')?.hasError('currency')) {
              <mat-error>Invalid currency format.</mat-error>
            } @else if (row.get('amount')?.hasError('required') || row.get('amount')?.hasError('min')) {
              <mat-error>Please enter an amount greater than zero.</mat-error>
            }
          </mat-form-field>
          <button
            mat-icon-button
            type="button"
            class="rent-history__remove"
            [attr.aria-label]="'Remove month ' + (i + 1) + ' of ' + rows.length"
            [disabled]="(i === 0 && required) || rows.disabled"
            (click)="remove(i)">
            <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
          </button>
        </div>
      }
      @if (rows.hasError('uniquePastDueRentHistory')) {
        <p class="rent-history__error">Please select unique months.</p>
      }
      <button mat-flat-button color="primary" type="button" [disabled]="rows.invalid || rows.disabled || full" (click)="add()">
        Add another month
      </button>
    </fieldset>
  `,
  styles: [
    `
      .rent-history {
        border: 1px solid var(--c-gray-85, #d9d9d9);
        border-radius: 8px;
        padding: 0.75rem 1rem 1rem;
        margin: 0;
        min-width: 0;
      }
      .rent-history__legend {
        padding: 0 0.25rem;
        font-weight: 500;
      }
      .rent-history__lead {
        margin: 0 0 0.75rem;
        color: var(--c-gray-40, #666);
      }
      .rent-history__row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr auto;
        gap: 0.75rem;
        align-items: start;
      }
      .rent-history__remove {
        margin-top: 0.5rem;
      }
      .rent-history__error {
        color: var(--c-error, #d32f2f);
        font-weight: 500;
        margin: 0 0 0.75rem;
      }
      @media (max-width: 767px) {
        .rent-history__row {
          grid-template-columns: 1fr 1fr;
        }
      }
    `
  ]
})
export class RentHistoryBlockComponent implements OnChanges {
  /** The rows. Null on the builder canvas, where an inert example row is drawn instead. */
  @Input() array: FormArray | null = null;

  @Input() label = 'Rent Owed, by month';

  @Input() hint: string | null | undefined = null;

  @Input() required = false;

  @Input() compact = true;

  @Input() builder = false;

  protected readonly months = MONTHS;

  /** This year and the four before it, as v1 offered. */
  protected readonly years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  /** What the canvas draws when there is no form behind it. */
  private readonly example = (() => {
    const example = new FormArray<FormGroup>([rentHistoryRow()]);
    example.disable({ emitEvent: false });
    return example;
  })();

  protected get rows(): FormArray<FormGroup> {
    return (this.array as FormArray<FormGroup> | null) ?? this.example;
  }

  ngOnChanges(): void {
    // A required history starts with its first month there to be filled in, as v1's did. An optional
    // one starts empty: a row with nothing in it is invalid, and would make "not answered" impossible.
    if (this.array && !this.builder && this.required && !this.array.length) {
      this.add();
      STARTING_ROW.set(this.array, this.rows.at(this.rows.length - 1));
    }
    // No longer required (an OPTIONAL rule): the untouched month this block added is not an answer the
    // filer gave, and left there it would keep an optional question invalid. Remembered with the array,
    // not this instance — the wizard builds a new block each time it returns to the page.
    const starting = this.array ? STARTING_ROW.get(this.array) : undefined;
    if (this.array && !this.required && starting && this.array.length === 1 && this.array.at(0) === starting) {
      if (starting.pristine) {
        this.array.removeAt(0);
      }
      STARTING_ROW.delete(this.array);
    }
  }

  /** The field holds at most ten months; the button says so by going quiet. */
  protected get full(): boolean {
    return this.rows.length >= RENT_HISTORY_MAX_ROWS;
  }

  protected add(): void {
    if (this.full) {
      return;
    }
    this.rows.push(rentHistoryRow());
  }

  /** The first month stays while the history is required; an optional one may go back to nothing. */
  protected remove(index: number): void {
    if (index > 0 || !this.required) {
      this.rows.removeAt(index);
    }
  }
}
